import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2, TrendingUp, Users, Package, Headphones, ArrowUpRight } from "lucide-react";
import { csuiteApi } from "../../../lib/csuiteApi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const money = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

/**
 * Microsoft Dynamics 365 executive cockpit.
 * Four sub-sections: Finance & Ops, Sales, Supply Chain, Customer Service.
 * Data is presentation-grade mock served by /api/platinum/dynamics365 —
 * swap for the real D365 OData endpoints when a customer wires their tenant.
 */
export default function D365Cockpit() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { csuiteApi.dynamics365().then(setD).catch(e => setErr(e.message || "Failed")); }, []);
  if (err) return <div className="p-6 text-red-700 flex items-center gap-2"><AlertTriangle size={16}/>{err}</div>;
  if (!d) return <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-sky-700"/></div>;
  const f = d.finance, s = d.sales, sc = d.supplyChain, cs = d.customerService;
  return (
    <div className="space-y-6" data-testid="d365-cockpit">
      <Header source={d.source} />
      {/* Finance & Ops */}
      <Section title="Finance & Operations" eyebrow="Module 01">
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <Tile label="Revenue · MTD" value={money(f.revenueMtd)} tone="blue" />
          <Tile label="Revenue · QTD" value={money(f.revenueQtd)} tone="blue" />
          <Tile label="EBITDA %"      value={`${f.ebitdaPct}%`}   tone="green" />
          <Tile label="Cash on hand"  value={money(f.cashOnHand)} tone="neutral" sub={`AR days · ${f.arDays}`} />
        </div>
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Revenue · trailing 12 months</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer><LineChart data={f.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v/1_000_000}M`} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 12 }}/>
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle"/>
              <Line dataKey="revenue" stroke="#0369A1" strokeWidth={2} name="Revenue" dot={false}/>
              <Line dataKey="ebitda"  stroke="#06B6D4" strokeWidth={2} name="EBITDA"  dot={false}/>
              <Line dataKey="gross"   stroke="#22D3EE" strokeWidth={2} name="Gross"   dot={false}/>
            </LineChart></ResponsiveContainer>
          </div>
        </div>
      </Section>
      {/* Sales */}
      <Section title="Sales · Pipeline & Regions" eyebrow="Module 02">
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <Tile label="Pipeline"        value={money(s.pipelineTotal)} tone="blue" />
          <Tile label="Win rate"        value={`${s.winRate}%`}        tone="green" />
          <Tile label="Avg deal size"   value={money(s.avgDealSize)}   tone="neutral" />
          <Tile label="Cycle days"      value={`${s.salesCycleDays}d`} tone="neutral" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Pipeline by Stage</div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer><BarChart data={s.byStage}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="stage" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v/1_000_000}M`} />
                <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 12 }}/>
                <Bar dataKey="value" fill="#0369A1" radius={[3,3,0,0]} />
              </BarChart></ResponsiveContainer>
            </div>
          </div>
          <div className="card p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Regional Revenue</div>
            <table className="w-full text-sm">
              <thead className="text-zinc-500 border-b border-[var(--border)]"><tr>
                <th className="text-left py-1.5">Region</th><th className="text-right">Revenue</th><th className="text-right">Growth</th>
              </tr></thead>
              <tbody>
                {s.regions.map(r => (
                  <tr key={r.region} className="border-b border-[var(--border)] last:border-0">
                    <td className="py-1.5 font-medium">{r.region}</td>
                    <td className="text-right font-mono">{money(r.revenue)}</td>
                    <td className={`text-right font-mono ${r.growth >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {r.growth >= 0 ? "+" : ""}{r.growth}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
      {/* Supply Chain */}
      <Section title="Supply Chain Management" eyebrow="Module 03" icon={<Package size={16}/>}>
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <Tile label="OTIF %"        value={`${sc.onTimeInFullPct}%`} tone="green" />
          <Tile label="Inventory turns" value={sc.inventoryTurns}      tone="blue" />
          <Tile label="Backlog days"    value={`${sc.backlogDays}d`}   tone="neutral" />
          <Tile label="Carrier perf"    value={`${sc.carrierPerf}%`}   tone="green" />
        </div>
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Top Supply-Chain Risks</div>
          <ul className="text-sm divide-y divide-[var(--border)]">
            {sc.topRisks.map((r, i) => (
              <li key={i} className="py-2 flex items-center justify-between">
                <span>{r.item}</span>
                <span className="flex items-center gap-3">
                  <span className={`pill text-[10px] ${r.severity === "High" ? "pill-red" : r.severity === "Medium" ? "pill-amber" : "pill-neutral"}`}>{r.severity}</span>
                  <span className="text-xs text-zinc-500 font-mono">ETA {r.eta}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Section>
      {/* Customer Service */}
      <Section title="Customer Service" eyebrow="Module 04" icon={<Headphones size={16}/>}>
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <Tile label="CSAT"            value={`${cs.csatPct}%`}                   tone="green" />
          <Tile label="First-contact"   value={`${cs.firstContactResolvedPct}%`}   tone="blue" />
          <Tile label="Avg handle"      value={`${Math.round(cs.avgHandleTimeS/60)}m ${cs.avgHandleTimeS % 60}s`} tone="neutral" />
          <Tile label="Open cases"      value={cs.openCases}                        tone="neutral" sub={`${cs.escalated} escalated`} />
        </div>
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Cases by Category · SLA %</div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer><BarChart data={cs.byCategory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="category" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 12 }}/>
              <Bar dataKey="count" fill="#0EA5E9" name="Cases" radius={[3,3,0,0]}/>
              <Bar dataKey="sla" fill="#22D3EE" name="SLA %" radius={[3,3,0,0]}/>
            </BarChart></ResponsiveContainer>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Header({ source }) {
  const isLive = source === "live";
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Microsoft Dynamics 365</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Executive Cockpit</h2>
      </div>
      <span className={`pill text-[10px] ${isLive ? "pill-green" : "pill-neutral"}`} data-testid={`d365-source-${source || "mock"}`}>
        {isLive ? "Live · your D365 tenant" : "Demonstration data · connect your D365 tenant on Connections to swap"}
      </span>
    </div>
  );
}
function Section({ title, eyebrow, children, icon }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        {eyebrow && <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">{eyebrow}</div>}
        <h3 className="font-display text-base font-bold tracking-tight text-zinc-900">{title}</h3>
      </div>
      {children}
    </section>
  );
}
function Tile({ label, value, sub, tone }) {
  const cls = tone === "green" ? "text-emerald-700" : tone === "blue" ? "text-sky-700" : tone === "red" ? "text-red-700" : "text-zinc-900";
  return (
    <div className="card p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}
